import React, { useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRegionalLink } from 'hooks/useRegionalLink';
import { useRegion } from 'contexts/RegionContext';
import omit from 'lodash/omit';
import { initialTableShow } from 'utils/settings';
import { access, hasAccess, resources } from 'utils/permissions';
import {
  Text,
  Member,
  DateTime,
  Button,
  WithTooltip
} from 'components/common/Elements/Elements';
import { CustomIcon, _palette } from '@p81-common/ui';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import { getInitialsFull } from 'utils/common';
import { links } from 'data/constants';
import AdditionalContent from '../parts/PageTable/AdditionalContent/AdditionalContent';
import ModalFeature from '../parts/ModalFeature/ModalFeature';
import DeleteConfirmModal from '../parts/DeleteConfirmModal/DeleteConfirmModal';
import classes from '../FeatureList.module.scss';
import useStatusCellStyles from './styles';
import { GridRenderCellParams } from '@mui/x-data-grid';

export const statusPars: Record<string, string> = {
  New: 'New',
  ReadyToDev: 'Ready To Dev',
  ReadyToAssign: 'Ready To Assign',
  Assigned: 'Assigned',
  GlobalAssigned: 'General Availability'
};

export const optionStatus = [
  { value: 'New', label: 'New' },
  { value: 'ReadyToDev', label: 'Ready To Dev' },
  { value: 'ReadyToAssign', label: 'Ready To Assign' },
  { value: 'GlobalAssigned', label: 'General Availability' }
];

interface FeatureRow {
  _id: string;
  name: string;
  key: string;
  status: string;
  description: string;
  updatedByName?: string;
  updatedByEmail?: string;
  ownerName?: string;
  ownerEmail?: string;
  lastModified: string;
  customerIdsCount: number;
  autoAssign?: boolean;
}

interface CreateTableConfigParams {
  onOpenAdditionalModal: (info: any) => void;
  setIsOpenAdditional: (value: boolean) => void;
  requestForFilteredData: () => void;
  onDeleteFeature: (id: string) => Promise<void>;
}

// Helper to create member object
const createMember = (company: FeatureRow) => {
  return {
    fullName: company.updatedByName ? company.updatedByName : 'NA',
    description: company.updatedByEmail ? company.updatedByEmail : 'NA',
    initials: getInitialsFull(company.updatedByName)
  };
};

// Avatar otherwise hashes the initials into colorList, and 'NA' lands on a
// green that reads like a real, assigned owner. Neutral grey ($color-disabled)
// keeps the unassigned rows quiet.
const NO_OWNER_AVATAR_COLOR = '#b3bac8';

// Feature flags created before the owner field existed have none, and render NA
// until somebody assigns one.
const createOwnerMember = (company: FeatureRow) => {
  return {
    fullName: company.ownerName ? company.ownerName : 'NA',
    description: company.ownerEmail ? company.ownerEmail : 'NA',
    initials: getInitialsFull(company.ownerName),
    initialsColor: company.ownerName ? undefined : NO_OWNER_AVATAR_COLOR
  };
};

// Check if button should be disabled
const isDisabledButton = (status: string) => {
  return !['Assigned', 'ReadyToAssign', 'General Availability'].includes(
    status
  );
};

// Render cell functions
const renderNameCell = (params: GridRenderCellParams<any, FeatureRow>) => {
  return (
    <div>
      <Text type="p" weight="b" className={classes.feature}>
        {params.row.name}
      </Text>
      <Text type="p" size={2} className={classes.key}>
        {params.row.key}
      </Text>
    </div>
  );
};

const StatusCell = ({ row }: { row: FeatureRow }) => {
  const { t } = useTranslation(['activity']);
  const statusClasses = useStatusCellStyles();
  return (
    <span className={statusClasses.statusCell}>
      {statusPars[row.status]}
      {row.autoAssign && (
        <WithTooltip text={t('activity:featureFlag.AUTO_ASSIGN_BADGE_TOOLTIP')}>
          {(<span className={statusClasses.autoAssignBadge}>AUTO</span>) as any}
        </WithTooltip>
      )}
    </span>
  );
};

const renderStatusCell = (params: GridRenderCellParams<any, FeatureRow>) => {
  return <StatusCell row={params.row} />;
};

const renderDescriptionCell = (
  params: GridRenderCellParams<any, FeatureRow>
) => {
  return params.row.description;
};

const renderUpdatedByCell = (params: GridRenderCellParams<any, FeatureRow>) => {
  return <Member member={createMember(params.row)} />;
};

const renderOwnerCell = (params: GridRenderCellParams<any, FeatureRow>) => {
  return <Member member={createOwnerMember(params.row)} />;
};

const renderLastModifiedCell = (
  params: GridRenderCellParams<any, FeatureRow>
) => {
  const DateTimeComponent = DateTime as any;
  return <DateTimeComponent date={params.row.lastModified} time={true} />;
};

const renderCustomerCountCell = (
  params: GridRenderCellParams<any, FeatureRow>,
  onOpenAdditionalModal: (info: any) => void
) => {
  const { row } = params;

  if (!isNaN(row.customerIdsCount) && row.customerIdsCount > 0) {
    return (
      <Button
        className={classes.appliedTo_button}
        onClick={() =>
          onOpenAdditionalModal({
            content: <AdditionalContent data={row._id} />
          })
        }
      >
        {row.customerIdsCount}
      </Button>
    );
  }

  if (!isNaN(row.customerIdsCount) && row.status === 'General Availability') {
    return 'GA';
  }

  return row.customerIdsCount;
};

// Actions cell component with icon buttons and delete confirmation
const ActionsCell = ({
  row,
  onOpenAdditionalModal,
  setIsOpenAdditional,
  requestForFilteredData,
  onDeleteFeature
}: {
  row: FeatureRow;
  onOpenAdditionalModal: (info: any) => void;
  setIsOpenAdditional: (value: boolean) => void;
  requestForFilteredData: () => void;
  onDeleteFeature: (id: string) => Promise<void>;
}) => {
  const history = useHistory();
  const generateRegionalPath = useRegionalLink();
  const { region } = useRegion();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeletingFeature, setIsDeletingFeature] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);

  const handleDeleteClick = () => {
    setDeleteModalOpen(true);
  };

  const handleEditClick = () => {
    onOpenAdditionalModal({
      content: (
        <ModalFeature
          data={row}
          setIsOpenAdditional={setIsOpenAdditional}
          requestForFilteredData={requestForFilteredData}
        />
      )
    });
  };

  const handleAssignClick = () => {
    if (!isDisabledButton(row.status)) {
      history.push(generateRegionalPath(`/product/feature-flag/${row.name}`), {
        // customerIds is unused by the page and would be copied into every
        // history entry the page writes.
        dataSelectCell: omit(row, 'customerIds'),
        countAssignedCustomers: row.customerIdsCount,
        // The row's _id is only valid in this region; the Assigned page
        // re-resolves the flag by key when it is shown in another one.
        region: region.code
      });
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeletingFeature(true);
    try {
      await onDeleteFeature(row._id);
      setDeleteModalOpen(false);
    } catch (error) {
      console.error('Delete failed:', error);
    } finally {
      setIsDeletingFeature(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
  };

  const hasDeleteAccess = hasAccess([
    resources.PRODUCT_FEATURE_FLAG,
    access.DELETE
  ]);

  const showAssign = row.status !== 'General Availability';
  const showDelete = hasDeleteAccess && row.status !== 'Assigned';
  const assignDisabled = isDisabledButton(row.status);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
  };

  const handleDeleteMenuClick = () => {
    handleDeleteClick();
    handleMenuClose();
  };

  const handleEditMenuClick = () => {
    handleEditClick();
    handleMenuClose();
  };

  const handleAssignMenuClick = () => {
    handleAssignClick();
    handleMenuClose();
  };

  return (
    <div>
      <IconButton
        ref={ref}
        onClick={toggleMenu}
        size="small"
        sx={{
          backgroundColor: 'white',
          '&:hover': {
            backgroundColor: 'white'
          }
        }}
      >
        <CustomIcon
          src={`${links.imagesBaseUrl}/icons/icon_3dots.svg`}
          style={{ transform: 'rotate(90deg)' }}
        />
      </IconButton>

      <Popover
        anchorEl={ref.current}
        open={menuOpen}
        onClose={handleMenuClose}
        disableScrollLock={false}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        PaperProps={{
          sx: {
            maxHeight: 300,
            '&::before': {
              left: 'calc(50% - 5px) !important'
            }
          }
        }}
      >
        <MenuList>
          <MenuItem onClick={handleEditMenuClick}>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
          {showAssign && (
            <MenuItem onClick={handleAssignMenuClick} disabled={assignDisabled}>
              <ListItemText>Assign</ListItemText>
            </MenuItem>
          )}
          {showDelete && (
            <MenuItem onClick={handleDeleteMenuClick}>
              <ListItemText sx={{ color: _palette.error[500] }}>
                Delete
              </ListItemText>
            </MenuItem>
          )}
        </MenuList>
      </Popover>

      <DeleteConfirmModal
        open={deleteModalOpen}
        featureName={row.name}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isLoading={isDeletingFeature}
      />
    </div>
  );
};

export const createMainTableConf = ({
  onOpenAdditionalModal,
  setIsOpenAdditional,
  requestForFilteredData,
  onDeleteFeature
}: CreateTableConfigParams) => {
  const hasFeatureFlagWriteAccess = hasAccess([
    resources.PRODUCT_FEATURE_FLAG,
    access.WRITE
  ]);

  return [
    {
      sortable: true,
      field: 'name',
      headerName: 'Feature Name',
      show: true,
      width: 280,
      align: 'left' as const,
      renderCell: renderNameCell
    },
    {
      sortable: true,
      field: 'status',
      headerName: 'Status',
      show: true,
      width: 180,
      align: 'left' as const,
      renderCell: renderStatusCell
    },
    {
      sortable: false,
      field: 'description',
      headerName: 'Description',
      show: true,
      width: 350,
      align: 'left' as const,
      renderCell: renderDescriptionCell
    },
    {
      sortable: true,
      field: 'ownerName',
      headerName: 'Owner',
      show: true,
      width: 200,
      align: 'left' as const,
      renderCell: renderOwnerCell
    },
    {
      sortable: true,
      field: 'updatedByName',
      headerName: 'Updated By',
      show: true,
      width: 200,
      align: 'left' as const,
      renderCell: renderUpdatedByCell
    },
    {
      sortable: true,
      field: 'lastModified',
      headerName: 'Updated At',
      show: true,
      width: 180,
      align: 'left' as const,
      renderCell: renderLastModifiedCell
    },
    {
      sortable: true,
      field: 'customerIdsCount',
      headerName: 'Customers',
      show: true,
      width: 150,
      align: 'left' as const,
      renderCell: (params: GridRenderCellParams<any, FeatureRow>) =>
        renderCustomerCountCell(params, onOpenAdditionalModal)
    },
    {
      sortable: false,
      resizable: false,
      field: 'actions',
      type: 'actionButtons',
      headerName: '',
      show: hasFeatureFlagWriteAccess,
      noAccess: !hasFeatureFlagWriteAccess,
      width: 80,
      renderCell: (params: GridRenderCellParams<any, FeatureRow>) => (
        <ActionsCell
          row={params.row}
          onOpenAdditionalModal={onOpenAdditionalModal}
          setIsOpenAdditional={setIsOpenAdditional}
          requestForFilteredData={requestForFilteredData}
          onDeleteFeature={onDeleteFeature}
        />
      )
    }
  ];
};

// Keep the old mainTableConf for backwards compatibility
export const mainTableConf = [
  {
    isSortable: true,
    field: 'name',
    title: 'Feature Name',
    show: true,
    width: 160
  },
  {
    isSortable: true,
    field: 'status',
    title: 'Status',
    show: true,
    width: 152
  },
  {
    isSortable: false,
    field: 'description',
    title: 'Description',
    show: true,
    width: 312
  },
  {
    isSortable: true,
    field: 'updatedByName',
    title: 'Updated By',
    show: true,
    width: 136
  },
  {
    isSortable: true,
    field: 'lastModified',
    title: 'Updated At',
    show: true,
    width: 136
  },
  {
    isSortable: true,
    field: 'customerIdsCount',
    title: 'Customers',
    show: true,
    width: 208
  },
  {
    isSortable: false,
    field: 'buttons',
    title: 'Actions',
    show: hasAccess([resources.PRODUCT_FEATURE_FLAG, access.WRITE])
  }
];

export const defaultFilters = {
  search: '',
  status: [],
  updatedByName: [],
  ownerName: [],
  isTestTenant: '',
  autoAssign: ''
};

export const defaultNavigation = {
  fieldName: 'lastModified',
  direction: initialTableShow.direction,
  currentPage: initialTableShow.currentPage,
  itemsPerPage: initialTableShow.itemsPerPage
};

export const featureFlagsStatuses = {
  Assigned: 'Assigned',
  GlobalAssigned: 'GlobalAssigned',
  New: 'New',
  ReadyToDev: 'ReadyToDev',
  ReadyToAssign: 'ReadyToAssign'
};
